import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import permissionService from '../services/permissionService';

export const usePermissions = () => {
  const [permissions, setPermissionsState] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Wrapper para setPermissions con logging
  const setPermissions = (newPermissions) => {
    console.log('🔄 CAMBIANDO PERMISOS:', {
      from: permissions.length,
      to: newPermissions?.length || 0,
      newPermissions: newPermissions
    });
    setPermissionsState(newPermissions);
  };

  console.log('🔍 usePermissions montado - esperando evento de login...');

  // Escuchar evento de refresh de permisos después del login
  useEffect(() => {
    const handlePermissionsRefreshed = (event) => {
      console.log('🔔 Evento de refresh de permisos recibido:', event.detail);
      
      // Actualizar estado con los nuevos permisos
      if (event.detail?.permissions) {
        setUserRole(event.detail.permissions.role);
        setError(null);
        setLoading(false);
        
        console.log('✅ Permisos actualizados desde evento:', {
          screens: event.detail.permissions.screens?.length || 0,
          role: event.detail.permissions.role?.name
        });
        
        setPermissions(event.detail.permissions.screens || []);
      }
    };

    // Agregar listener para el evento personalizado
    window.addEventListener('permissionsRefreshed', handlePermissionsRefreshed);

    // Cleanup: remover listener al desmontar
    return () => {
      window.removeEventListener('permissionsRefreshed', handlePermissionsRefreshed);
    };
  }, []);

  // Cargar permisos al montar el componente (SOLO PARA TESTING)
  useEffect(() => {
    console.log('🔄 usePermissions montado - verificando si hay token');
    console.log('🔍 Estado inicial:', {
      loading,
      permissionsCount: permissions.length,
      hasToken: !!sessionStorage.getItem('authToken')
    });
    
    // Solo cargar si hay token (para testing)
    const token = sessionStorage.getItem('authToken');
    if (token) {
      console.log('🚀 Token encontrado, cargando permisos directamente...');
      loadPermissionsFromServer();
    } else {
      console.log('❌ No hay token, esperando evento de login...');
    }
  }, []);

  // YA NO DETECTAMOS CAMBIOS AQUÍ - Lo hace el Navbar
  // useEffect(() => {
  //   // Lógica movida al Navbar
  // }, [location.pathname]);

  // Función para extraer la sección principal de la ruta
  const getMainSection = (pathname) => {
    const parts = pathname.split('/').filter(Boolean);
    return parts[0] || 'home'; // Primera parte de la ruta o 'home' por defecto
  };

  // Función para cargar permisos desde el servidor (NUEVA)
  const loadPermissionsFromServer = async () => {
    try {
      setLoading(true);
      console.log('🔄 EJECUTANDO loadPermissionsFromServer...');
      console.log('🔍 Token disponible:', sessionStorage.getItem('authToken') ? 'SÍ' : 'NO');
      
      console.log('🚀 Llamando a permissionService.getUserPermissions()...');
      const userPermissions = await permissionService.getUserPermissions();
      
      console.log('📥 Respuesta recibida del servicio:', userPermissions);
      
      console.log('🔍 Actualizando estado de permisos:', {
        screensReceived: userPermissions.screens?.length || 0,
        roleReceived: userPermissions.role?.name || 'NO_ROLE'
      });
      
      setPermissions(userPermissions.screens || []);
      setUserRole(userPermissions.role);
      setError(null);
      
      console.log('✅ Estado actualizado - Permisos recargados por cambio de ruta:', {
        screens: userPermissions.screens?.length || 0,
        role: userPermissions.role?.name
      });
      
    } catch (err) {
      setError('Error loading permissions');
      console.error('❌ ERROR COMPLETO en loadPermissionsFromServer:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error response:', err.response);
    } finally {
      setLoading(false);
      console.log('🏁 loadPermissionsFromServer terminado');
    }
  };

  const loadPermissions = async () => {
    console.log('⚠️ loadPermissions llamado - PERO YA NO SE USA');
    console.log('🔍 Stack trace:', new Error().stack);
    console.log('❌ Esta función ya no debería ejecutarse - solo por evento de login');
    
    // NO hacer nada - solo por evento de login
    return;
  };

  // Función para refrescar permisos manualmente (DESHABILITADA)
  const refreshPermissions = async () => {
    console.log('⚠️ refreshPermissions llamado - PERO YA NO SE USA');
    console.log('❌ Solo se debe usar el evento de login');
    return;
  };

  const hasPermission = (route, permissionType = 'read') => {
    console.log(`🔍 hasPermission llamado con:`, { route, permissionType, permissionsCount: permissions.length });
    
    // Obtener información del usuario desde localStorage
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const userRole = userInfo?.role?.name;
    
    // EXCEPCIÓN: Si es ADMINISTRADOR, dar acceso a rutas críticas sin validar backend
    const isAdmin = userRole === 'ADMINISTRADOR' || 
                   userRole === 'ADMIN' || 
                   userRole === 'Administrator' ||
                   userInfo?.role?.id === 1;
    
    if (isAdmin) {
      console.log(`👑 Usuario ADMINISTRADOR - acceso automático a ${route}`);
      return true;
    }
    
    if (!permissions || permissions.length === 0) {
      console.log(`❌ No hay permisos cargados para verificar: ${route}`);
      return false;
    }

    // SOLO búsqueda exacta - eliminada lógica problemática de includes()
    const permission = permissions.find(p => p.route === route);
    
    console.log(`🔍 Permiso encontrado para ${route}:`, permission);
    
    if (!permission) {
      console.log(`❌ No se encontró permiso exacto para ruta: ${route}`);
      console.log(`📋 Permisos disponibles:`, permissions.map(p => p.route));
      return false;
    }

    // Simplificado: solo validar canRead
    const hasAccess = permission.canRead || false;
    
    console.log(`🔐 Permiso read para ${route}: ${hasAccess ? '✅' : '❌'}`, {
      canRead: permission.canRead
    });
    return hasAccess;
  };

  const getAccessibleRoutes = () => {
    const accessibleRoutes = permissions
      .filter(p => p.canRead)
      .map(p => ({
        route: p.route,
        name: p.name,
        icon: p.icon,
        screenId: p.screenId
      }));
      
    console.log('📋 Rutas accesibles:', accessibleRoutes);
    return accessibleRoutes;
  };

  const canAccess = async (route) => {
    return await permissionService.hasScreenAccess(route);
  };

  const canWrite = async (route) => {
    return await permissionService.canWrite(route);
  };

  const canDelete = async (route) => {
    return await permissionService.canDelete(route);
  };

  const canAdmin = async (route) => {
    return await permissionService.canAdmin(route);
  };

  return {
    permissions,
    userRole,
    loading,
    error,
    hasPermission,
    getAccessibleRoutes,
    canAccess,
    canWrite,
    canDelete,
    canAdmin,
    reload: loadPermissions,
    refreshPermissions, // Nueva función para refrescar
    clearCache: () => permissionService.clearCache()
  };
};

export default usePermissions;
